/**
 * Service Models for a given assessment (Phase 1 — Service Model Foundation).
 *
 * Reads the ASSESSMENT SNAPSHOT (`assessment_infrastructure_scope`) via
 * `assessment_get_service_model_state`. There is no portfolio fallback: editing
 * a portfolio never retroactively changes an existing assessment, and an
 * assessment with no snapshot is reported as explicitly unconfigured rather
 * than silently inheriting live portfolio values.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AssessmentServiceModelState {
  models: string[];
  snapshot: string[] | null;
  confirmed: boolean;
  confirmedAt: string | null;
  confirmedBy: string | null;
  usesServiceModels: boolean;
  requiresServiceModels: boolean;
  /** True when the questionnaire uses models but none are set for this assessment. */
  unconfigured: boolean;
}

const EMPTY: AssessmentServiceModelState = {
  models: [],
  snapshot: null,
  confirmed: false,
  confirmedAt: null,
  confirmedBy: null,
  usesServiceModels: false,
  requiresServiceModels: false,
  unconfigured: false,
};

export function useAssessmentServiceModels(assessmentId: string | null | undefined) {
  const [state, setState] = useState<AssessmentServiceModelState>(EMPTY);
  const [loading, setLoading] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!assessmentId) {
      setState(EMPTY);
      return;
    }
    setLoading(true);
    const { data, error } = await (supabase.rpc as any)(
      "assessment_get_service_model_state",
      { p_assessment_id: assessmentId },
    );
    if (error) {
      console.warn("[useAssessmentServiceModels] RPC error:", error);
      setState(EMPTY);
    } else {
      const row = Array.isArray(data) ? data[0] : data;
      const models: string[] = row?.models ?? [];
      setState({
        models,
        snapshot: row?.snapshot ?? null,
        confirmed: !!row?.confirmed,
        confirmedAt: row?.confirmed_at ?? null,
        confirmedBy: row?.confirmed_by ?? null,
        usesServiceModels: !!row?.uses_service_models,
        requiresServiceModels: !!row?.requires_service_models,
        unconfigured: !!row?.uses_service_models && models.length === 0,
      });
    }
    setLoading(false);
  }, [assessmentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { ...state, loading, reload: load };
}

/**
 * Back-compat wrapper for the previous hook name/shape.
 * @deprecated Use useAssessmentServiceModels.
 */
export function usePortfolioServiceModels(assessmentId: string | null | undefined) {
  const { models, loading } = useAssessmentServiceModels(assessmentId);
  return { models, loading };
}
